import { useGLTF } from '@react-three/drei'
import { useGraph } from '@react-three/fiber'
import proj4 from 'proj4'
import { useScene } from 'providers/SceneProvider'
import { FC, useMemo } from 'react'
import { Material, MeshLambertMaterial, Vector3 } from 'three'
import { SkeletonUtils } from 'three-stdlib'
import { Slide, startPosition } from 'utils/getData'
import LocationInformation from './LocationInformation'

interface Props {
  slide: Slide
}

const SlideItem: FC<Props> = ({ slide }) => {
  const { selectedSlide } = useScene()
  const glb = useGLTF(slide.model ? slide.model.modelPath : '', false, true)
  const model = slide?.model
  const { scene } = glb
  const clone = useMemo(() => SkeletonUtils.clone(scene), [scene])
  const { nodes } = useGraph(clone)

  if (!model) {
    return null
  }
  const lat = slide.cameraParameters.targetPosition.lat
  const lng = slide.cameraParameters.targetPosition.lng

  const origoGoogle = proj4('WGS84', 'GOOGLE', [startPosition.lng, startPosition.lat])
  const targetGoogle = proj4('WGS84', 'GOOGLE', [lng, lat])

  const factor = 0.52
  const x = (targetGoogle[0] - origoGoogle[0]) * factor
  const y = (targetGoogle[1] - origoGoogle[1]) * factor
  const newPosition = new Vector3(x + model.position.x, y + model.position.y, 0)

  return (
    <group>
      <LocationInformation position={newPosition} />
      <group scale={[model.scale, model.scale, model.scale]} position={newPosition} rotation={[0, 0, model.rotationZ]}>
        {/* </group> */}
        {Object.keys(nodes).map((key) => {
          const isInFocus = !Boolean(selectedSlide.model) || selectedSlide.id === slide.id
          let color = 0xffffff
          let opacity = 1

          if (!isInFocus) {
            color = 0x7777777
            opacity = 0.1
          }
          if (selectedSlide.visualizationType === 'area' && isInFocus) {
            color = 0xfd852a
            opacity = 0.4
          }
          if (selectedSlide.visualizationType === 'marker' && isInFocus) {
            color = 0xff0000
            opacity = 1
          }

          const material: Material = new MeshLambertMaterial({
            ...(nodes[key] as any).material,
            opacity,
            transparent: isInFocus ? true : true,
            color,
          })
          return <primitive key={key} object={nodes[key]} material={material} />
        })}
      </group>
    </group>
  )
}

export default SlideItem
